import React, { useState, useEffect } from "react";
import {
  Filter,
  Download,
  RefreshCw,
  LogIn,
  LogOut,
  Clock,
  User,
  Shield,
  Users,
  FileText,
} from "lucide-react";
import { toast } from "react-toastify";
import {
  exportLoginReportCSV,
  exportLoginReportExcel,
  exportLoginReportPDF,
  getLogginReport,
  // type LoginReportParams,
} from "../../../api/reportAPI";
import logger from "../../../utils/logger";
import {
  formatDisplayDate,
  formatDisplayDateTime,
  toISOForDB,
} from "../../../utils/Date";
import DateInput from "react-calendar-ui-kit";
import Pagination from "../../../utils/Pagination";

// Types
interface LoginRecord {
  fullName: string;
  role:
    | "Admin"
    | "SuperAdmin"
    | "Individual"
    | "Advocate"
    | "Judge"
    | "Reader"
    | "Controller";
  loginTime: string;
  logoutTime: string;
  sessionDuration: number;
  sessionDurationFormatted: string;
}

const LoginActivity: React.FC = () => {
  // State with proper types
  const [data, setData] = useState<LoginRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [error, setError] = useState("");
  const [exportLoading, setExportLoading] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);
  const [totalSessions, setTotalSessions] = useState(0);
  const [activeSessions, setActiveSessions] = useState(0);
  const [averageSessionMinutes, setAverageSessionMinutes] = useState(0);
  const [totalUsers, setTotalUsers] = useState(0);

  // Input fields (temporary filter values)
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [role, setRole] = useState("");
  // const today = new Date().toISOString().split("T")[0];

  // Applied filters
  const [filters, setFilters] = useState({ from: "", to: "", role: "" });
  const [resetLoading, setResetLoading] = useState(false);
  const fetchReports = async (page = 1, appliedFilters = filters) => {
    try {
      setError("");
      setLoading(false);
      setApplyLoading(false);

      const params = {
        page,
        limit: 10,
        ...(appliedFilters.from && { from: appliedFilters.from }),
        ...(appliedFilters.to && { to: appliedFilters.to }),
        ...(appliedFilters.role && { role: appliedFilters.role }),
      };

      const res = await getLogginReport(params);
      const apiData = res?.data?.data || [];
      setTotalSessions(res?.data?.meta?.total || 0);
      setActiveSessions(res?.data?.meta?.activeSessionsCount || 0);
      setAverageSessionMinutes(res?.data?.meta?.averageSessionMinutes || 0);
      setTotalUsers(res?.data?.meta?.totalUsersCount || 0);

      // Process session duration formatting
      const processedData = apiData.map((item: any) => {
        const { loginTime, logoutTime } = item;

        // If logoutTime is missing/null => Active session
        if (!logoutTime) {
          return {
            ...item,
            sessionDurationFormatted: "Active",
          };
        }

        // Calculate difference in minutes
        const loginDate = new Date(loginTime);
        const logoutDate = new Date(logoutTime);
        const diffMs = logoutDate.getTime() - loginDate.getTime();
        const diffMins = diffMs / (1000 * 60);

        return {
          ...item,
          sessionDurationFormatted: `${diffMins.toFixed(2)} mins`,
        };
      });

      setData(processedData);
      setCurrentPage(res?.data?.currentPage || 1);
      setTotalPages(res?.data?.totalPages || 1);
      setLoading(false);
    } catch (err) {
      setError("Failed to fetch reports.");
      setLoading(false);
      setApplyLoading(false);
    }
  };

  useEffect(() => {
    fetchReports(currentPage);
  }, [currentPage, filters]);

  const handleExport = async (type: "csv" | "excel" | "pdf") => {
    try {
      setExportLoading(true);
      setShowExportDropdown(false);
      const exportParams = {
        ...(filters.from && { from: filters.from }),
        ...(filters.to && { to: filters.to }),
        ...(filters.role && { role: filters.role }),
      };

      let res;

      if (type === "csv") {
        res = await exportLoginReportCSV(exportParams);
      } else if (type === "excel") {
        res = await exportLoginReportExcel(exportParams);
      } else {
        res = await exportLoginReportPDF(exportParams);
      }

      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `login-report.${type === "excel" ? "xlsx" : type}`;
      link.click();
    } catch (error) {
      logger.error("err", error);
    } finally {
      setExportLoading(false);
    }
  };

  const handleDateInputValidation = (
    value: string,
    setter: (val: string) => void,
  ) => {
    if (!value) return setter("");

    const isoValue = toISOForDB(value) ?? "";
    setter(isoValue);
  };
  const handleFilter = async (e: any) => {
    e.preventDefault();
    if (!from && to) {
      toast.error("Please select a From Date.");
      return;
    }

    if (from && to) {
      const fromYear = parseInt(from?.split("-")[0], 10);
      const toYear = parseInt(to?.split("-")[0], 10);

      if (fromYear > 9999 || toYear > 9999) {
        toast.error("Year must be only 4 digits.");
        return;
      }

      const fromDate = new Date(from);
      const toDate = new Date(to);
      if (toDate < fromDate) {
        toast.error("To Date cannot be earlier than From Date.");
        return;
      }
      setApplyLoading(true);

      setCurrentPage(1);
      setFilters({ from, to, role });
    }

    // Apply filters only if validation passes
    setCurrentPage(1);
    setFilters({ from, to, role });
  };

  const roleOptions = [
    "Admin",
    "SuperAdmin",
    "Individual",
    "Advocate",
    "Judge",
    "Reader",
    "Controller",
  ];

  useEffect(() => {
    setTotalPages(1);
  }, [filters, currentPage]);

  const getRoleBadge = (role: string) => {
    const roleConfig = {
      Admin: { color: "bg-blue-100 text-blue-800", icon: Shield },
      SuperAdmin: { color: "bg-purple-100 text-purple-800", icon: Shield },
      Individual: { color: "bg-gray-100 text-gray-800", icon: User },
      Advocate: { color: "bg-green-100 text-green-800", icon: Users },
      Judge: { color: "bg-indigo-100 text-indigo-800", icon: Shield },
      Reader: { color: "bg-yellow-100 text-yellow-800", icon: User },
      Controller: { color: "bg-red-100 text-red-800", icon: Shield },
    };

    const config =
      roleConfig[role as keyof typeof roleConfig] || roleConfig.Individual;
    const Icon = config.icon;

    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}
      >
        <Icon className="w-3 h-3 mr-1" />
        {role}
      </span>
    );
  };

  const getSessionStatusBadge = (
    logoutTime: string,
    sessionDurationFormatted: string,
  ) => {
    if (!logoutTime || sessionDurationFormatted === "Active") {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
          Active
        </span>
      );
    }

    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
        <Clock className="w-3 h-3 mr-1" />
        {sessionDurationFormatted}
      </span>
    );
  };

  const handleReset = async () => {
    try {
      setResetLoading(true);
      setFrom("");
      setTo("");
      setRole("");
      setCurrentPage(1);
      setFilters({ from: "", to: "", role: "" });
    } catch (error) {
      logger.error("error", error);
    } finally {
      setResetLoading(false);
    }
  };

  const formatAverageTime = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.floor(minutes % 60);
    return `${hours}h ${mins}m`;
  };

  return (
    <div className=" max-w-screen w-full min-h-screen p-6">
      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
        <div className="space-y-4">
          <div className="flex items-center space-x-2 mb-4">
            <Filter className="w-5 h-5 text-gray-500" />
            <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                From Date
              </label>
              <DateInput
                value={formatDisplayDate(from) ?? ""}
                onChange={(val: string) =>
                  handleDateInputValidation(val, setFrom)
                }
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                To Date
              </label>
              <DateInput
                value={formatDisplayDate(to) ?? ""}
                onChange={(val: string) =>
                  handleDateInputValidation(val, setTo)
                }
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Role
              </label>
              <div className="relative">
                <Shield className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <select
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                >
                  <option value="">All Roles</option>
                  {roleOptions.map((roleOption) => (
                    <option key={roleOption} value={roleOption}>
                      {roleOption}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-4 pt-4">
            <div className="sm:flex block items-center space-x-3">
              <button
                onClick={handleFilter}
                disabled={applyLoading}
                className="w-48 inline-flex items-center px-6 py-2 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 transition-colors disabled:opacity-50"
              >
                {applyLoading ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Filter className="w-4 h-4 mr-2" />
                )}
                {applyLoading ? "Applying..." : "Apply Filters"}
              </button>
              <button
                onClick={handleReset}
                disabled={resetLoading}
                className={` sm:mt-0 mt-3 inline-flex items-center px-6 py-2 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors
                                 ${resetLoading ? "opacity-50 cursor-not-allowed" : "opacity-100 cursor-pointer"}`}
              >
                <RefreshCw
                  className={`w-4 h-4 mr-2 ${resetLoading ? "animate-spin" : ""}`}
                />
                Reset
              </button>
            </div>

            <div className="relative">
              <button
                type="button"
                disabled={exportLoading}
                onClick={() => setShowExportDropdown(!showExportDropdown)}
                className="w-48 inline-flex items-center justify-center gap-2 px-6 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {exportLoading ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                <span className="whitespace-nowrap">
                  {exportLoading ? "Exporting..." : "Export Report"}
                </span>
              </button>

              {showExportDropdown && (
                <div className=" absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                  <button
                    onClick={() => handleExport("csv")}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                  >
                    Download as CSV
                  </button>
                  <button
                    onClick={() => handleExport("excel")}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                  >
                    Download as Excel
                  </button>
                  <button
                    onClick={() => handleExport("pdf")}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                  >
                    Download as PDF
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Total Sessions
              </p>
              <p className="text-3xl font-bold text-gray-900">
                {totalSessions}
              </p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <LogIn className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Active Sessions
              </p>
              <p className="text-3xl font-bold text-green-600">
                {activeSessions}
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <div className="w-6 h-6 bg-green-500 rounded-full animate-pulse"></div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Average Session
              </p>
              <p className="text-2xl font-bold text-purple-600">
                {formatAverageTime(averageSessionMinutes)}
              </p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <Clock className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Users</p>
              <p className="text-3xl font-bold text-orange-600">{totalUsers}</p>
            </div>
            <div className="p-3 bg-orange-100 rounded-lg">
              <Users className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Data Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            User Login Activities
          </h3>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 text-gray-400 animate-spin" />
            <span className="ml-2 text-gray-500">Loading activities...</span>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-12 text-red-600">
            <span>{error}</span>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    S.No.
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Login Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Logout Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Session Duration
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.length > 0 ? (
                  data.map((log, idx) => (
                    <tr
                      key={idx}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {(currentPage - 1) * 10 + idx + 1}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center text-white text-sm font-medium mr-3">
                            {log.fullName.charAt(0).toUpperCase()}
                          </div>
                          <div className="text-sm font-medium text-gray-900">
                            {log.fullName}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getRoleBadge(log.role)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-900">
                          <LogIn className="w-4 h-4 text-green-500 mr-2" />
                          {formatDisplayDateTime(log.loginTime)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-500">
                          {log.logoutTime ? (
                            <>
                              <LogOut className="w-4 h-4 text-red-500 mr-2" />
                              {formatDisplayDateTime(log.logoutTime)}
                            </>
                          ) : (
                            <span className="flex items-center text-green-600">
                              <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
                              Still Active
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getSessionStatusBadge(
                          log.logoutTime,
                          log.sessionDurationFormatted,
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-6 py-12 text-center text-gray-500"
                    >
                      <div className="flex flex-col items-center justify-center space-y-3">
                        <FileText className="h-12 w-12 text-gray-300" />
                        <h3 className="text-lg font-medium text-gray-600 mb-0">
                          No Login Activity Found
                        </h3>
                        <p className="text-xs text-gray-400">
                          Try adjusting your filters to see more results
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <div className="mt-4">
        {totalPages > 1 && (
          <Pagination
            page={currentPage}
            totalPages={totalPages}
            onPageChange={(page) => setCurrentPage(page)}
          />
        )}
      </div>
    </div>
  );
};

export default LoginActivity;
